import { BehaviorSubject, Observable, of, throwError, Subscription } from 'rxjs';
import { catchError, map, tap, finalize, filter, distinctUntilChanged } from 'rxjs/operators';
import {
  SavedItemCreate,
  SavedItemResponse,
  SavedItemsService,
  SavedItemUpdate,
} from 'ldt-state-storage-api';
import { NotificationService } from '../../shared/notification-service/notification.service';
import { AuthService } from '../../auth/service/auth.service';
import { Log } from '../../logger.service';
import {
  StoredItemConfig,
  BaseStoredItem,
} from '../../shared/components/stored-items-browser/stored-items-browser.component';

export interface StateStorageItem {
  id: string;
  scope?: string;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
}

export abstract class StateStorageBaseService<T extends StateStorageItem> {
  private cache$ = new BehaviorSubject<T[] | null>(null);
  private loading$ = new BehaviorSubject<boolean>(false);
  private error$ = new BehaviorSubject<Error | null>(null);
  private initialized = false;
  private orgChangeSubscription?: Subscription;

  public items$: Observable<T[] | null>;
  public isLoading$ = this.loading$.asObservable();
  public hasError$ = this.error$.asObservable();

  constructor(
    protected stateStorageService: SavedItemsService,
    protected authService: AuthService,
    protected notificationService: NotificationService
  ) {
    this.items$ = this.cache$.asObservable().pipe(
      tap((items) => {
        // If cache is null (cleared) and we're not initialized and not loading, fetch data.
        // Defer the fetch if we're in palMode or don't have an orgId yet — the org-change
        // subscription will kick off the initial load once an orgId is available.
        if (
          items === null &&
          !this.initialized &&
          !this.loading$.value &&
          !this.authService.palModeValue &&
          this.getOrgId()
        ) {
          this.initialized = true;
          this.refresh();
        }
      })
    );

    // Subscribe to org changes and clear cache when org changes
    let previousOrgId: string | null = null;
    this.orgChangeSubscription = this.authService.$getSelectedOrgId
      .pipe(
        distinctUntilChanged(),
        filter((orgId) => !!orgId)
      )
      .subscribe((orgId) => {
        if (previousOrgId !== null && previousOrgId !== orgId) {
          this.clearCache();
        } else if (
          previousOrgId === null &&
          !this.initialized &&
          !this.loading$.value &&
          this.cache$.value === null &&
          !this.authService.palModeValue
        ) {
          // First valid orgId arrived after a subscriber was already waiting on items$.
          this.initialized = true;
          this.refresh();
        }
        previousOrgId = orgId;
      });
  }

  abstract getResourceType(): string;
  abstract toApiModel(item: T): SavedItemCreate | SavedItemUpdate;
  abstract fromApiModel(response: SavedItemResponse): T | null;
  abstract getBrowserConfig(): StoredItemConfig<T & BaseStoredItem>;

  protected beforeCreate(item: T): T {
    return item;
  }

  protected afterCreate(item: T, response: SavedItemResponse): T | null {
    return this.fromApiModel(response);
  }

  protected beforeUpdate(id: string, item: T): T {
    return item;
  }

  protected afterUpdate(item: T, response: SavedItemResponse): T | null {
    return this.fromApiModel(response);
  }

  protected beforeDelete(id: string): void {}

  protected afterDelete(id: string): void {}

  protected getOrgId(): string {
    return this.authService.getSelectedOrgIdValue;
  }

  public refresh(): void {
    if (this.loading$.value) {
      return;
    }

    if (this.authService.palModeValue || !this.getOrgId()) {
      return;
    }

    this.loading$.next(true);
    this.error$.next(null);

    // 'undefined' is for scope, b/c we want to filter by resource type
    this.stateStorageService
      .listItems(this.getOrgId(), undefined, this.getResourceType())
      .pipe(
        map((response) => {
          const apiItems = (response || [])
            .map((item) => this.fromApiModel(item))
            .filter((item): item is T => item !== null); // Filter out null items
          return apiItems;
        }),
        tap((items) => {
          this.cache$.next(items);
        }),
        catchError((error) => {
          const errorMessage = `Error loading ${this.getResourceType()} items`;
          this.notificationService.error(errorMessage);
          Log.s(`${this.constructor.name}: refresh: error:`, 'error', {
            type: this.getResourceType(),
            error: error,
            orgId: this.getOrgId(),
          });
          this.error$.next(error);
          return of([]);
        }),
        finalize(() => {
          this.loading$.next(false);
        })
      )
      .subscribe();
  }

  // Creates the item in the selected org, or the org specified by orgOverride (used for copying)
  public create(item: T, orgOverride?: string): Observable<T> {
    const processedItem = this.beforeCreate(item);
    const apiModel = this.toApiModel(processedItem) as SavedItemCreate;
    const orgId = orgOverride || this.getOrgId();

    return this.stateStorageService.createItem(orgId, apiModel).pipe(
      map((response) => {
        const createdItem = this.afterCreate(processedItem, response);
        if (createdItem === null) {
          throw new Error(`Validation failed for created ${this.getResourceType()} item`);
        }
        return createdItem;
      }),
      tap((createdItem) => {
        if (orgId === this.getOrgId()) {
          const currentItems = this.cache$.value || [];
          this.cache$.next([...currentItems, createdItem]);
        }
      }),
      catchError((error) => {
        const errorMessage = `Error creating ${this.getResourceType()} item`;
        this.notificationService.error(errorMessage);
        Log.s(`${this.constructor.name}: create: error:`, 'error', {
          type: this.getResourceType(),
          error: error,
          orgId: this.getOrgId(),
          targetOrgId: orgOverride || '',
        });
        return throwError(() => error);
      })
    );
  }

  public update(id: string, item: T): Observable<T> {
    const processedItem = this.beforeUpdate(id, item);
    const apiModel = this.toApiModel(processedItem);

    return this.stateStorageService.updateItem(this.getOrgId(), id, apiModel).pipe(
      map((response) => {
        const updatedItem = this.afterUpdate(processedItem, response);
        if (updatedItem === null) {
          throw new Error(`Validation failed for updated ${this.getResourceType()} item`);
        }
        return updatedItem;
      }),
      tap((updatedItem) => {
        const currentItems = this.cache$.value || [];
        const nextItems = currentItems.map((item: T) => (item.id === id ? updatedItem : item));
        this.cache$.next(nextItems);
      }),
      catchError((error) => {
        const errorMessage = `Error updating ${this.getResourceType()} item`;
        this.notificationService.error(errorMessage);
        Log.s(`${this.constructor.name}: update: error:`, 'error', {
          type: this.getResourceType(),
          error: error,
          orgId: this.getOrgId(),
        });
        return throwError(() => error);
      })
    );
  }

  public delete(id: string): Observable<void> {
    this.beforeDelete(id);

    return this.stateStorageService.deleteItem(this.getOrgId(), id).pipe(
      tap(() => {
        const currentItems = this.cache$.value || [];
        const filteredItems = currentItems.filter((item: T) => item.id !== id);
        this.cache$.next(filteredItems);
        this.afterDelete(id);
      }),
      catchError((error) => {
        const errorMessage = `Error deleting ${this.getResourceType()} item.`;
        this.notificationService.error(errorMessage);
        Log.s(`${this.constructor.name}: delete: error:`, 'error', {
          type: this.getResourceType(),
          error: error,
          orgId: this.getOrgId(),
        });
        return throwError(() => error);
      })
    );
  }

  public getById(id: string): Observable<T | undefined> {
    if (this.authService.palModeValue) {
      return of(undefined);
    }

    return this.items$.pipe(
      filter((items) => items !== null),
      map((items) => items!.find((item: T) => item.id === id))
    );
  }

  public saveItem(id: string | null, item: T): Observable<T> {
    if (id === null) {
      return this.create(item);
    } else {
      return this.update(id, item);
    }
  }

  public clearCache(): void {
    this.cache$.next(null);
    this.initialized = false;
  }

  public forceRefresh(): void {
    this.initialized = false;
    this.refresh();
  }

  protected getCachedItems(): T[] | null {
    return this.cache$.value;
  }

  protected updateCache(items: T[]): void {
    this.cache$.next(items);
  }

  /**
   * Cleanup method to unsubscribe from org changes.
   * Should be called by subclasses in their ngOnDestroy method if they implement OnDestroy.
   */
  public destroy(): void {
    if (this.orgChangeSubscription) {
      this.orgChangeSubscription.unsubscribe();
    }
  }
}
